import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addTicketMessage, createTicket, fetchRegistration, fetchRegistrations, fetchTicketAdminUsers, fetchTicketMessageImage, fetchTickets, updateTicket } from '../services/api';
import { useGoogleAuth } from '../contexts/GoogleAuthContext';
import {
    AddTicketMessageRequest,
    AssigneeOption,
    CreateTicketRequest,
    Ticket,
    TicketMessage,
    TicketMessageDirection,
    TicketStatus,
} from '../types';

const statusOptions: Array<{ value: TicketStatus; label: string }> = [
    { value: 'new', label: 'Nieuw' },
    { value: 'in_progress', label: 'Bezig' },
    { value: 'waiting_customer', label: 'Wacht op klant' },
    { value: 'done', label: 'Klaar' },
];

const statusLabelMap: Record<TicketStatus, string> = {
    new: 'Nieuw',
    in_progress: 'Bezig',
    waiting_customer: 'Wacht op klant',
    done: 'Klaar',
};

const messageDirectionOptions: Array<{ value: TicketMessageDirection; label: string }> = [
    { value: 'customer', label: 'Van klant' },
    { value: 'agent', label: 'Van support' },
];

type MessageDraftState = AddTicketMessageRequest & { saving?: boolean };
type SortKey = 'ticketNumber' | 'subject' | 'customerName' | 'serialNumber' | 'status' | 'assignedTo' | 'updatedAt';
type SortDirection = 'asc' | 'desc';
type QuickFilterKey = 'all' | 'open' | 'waiting_customer' | 'overdue' | 'mine';
type MentionContext = { start: number; end: number; query: string };

const TICKET_POLL_INTERVAL_MS = 15 * 1000;
const TICKET_SEEN_STORAGE_PREFIX = 'ticketSeenMessageAtByTicket';
const MAX_TICKET_SUBJECT_LENGTH = 80;

function normalizePhone(raw?: string): string {
    if (!raw) {
        return '';
    }

    const cleaned = raw.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+')) {
        return cleaned.slice(1);
    }
    return cleaned;
}

function formatDateTime(value?: string): string {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString('nl-NL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function createDefaultDraft(): MessageDraftState {
    return {
        channel: 'internal',
        direction: 'agent',
        text: '',
        author: '',
        saving: false,
    };
}

function getStatusClass(status: TicketStatus): string {
    return `ticket-status-badge ticket-status-${status}`;
}

function normalizeIdentity(value?: string): string {
    return (value ?? '').trim().toLowerCase();
}

function parseTimestampMs(value?: string): number {
    if (!value) {
        return 0;
    }

    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : 0;
}

function getLatestMessage(ticket: Ticket): TicketMessage | null {
    if (!ticket.messages || ticket.messages.length === 0) {
        return null;
    }

    return ticket.messages.reduce((latest, current) => (
        parseTimestampMs(current.createdAt) > parseTimestampMs(latest.createdAt) ? current : latest
    ));
}

function findMentionContext(text: string, cursor: number): MentionContext | null {
    const safeCursor = Math.max(0, Math.min(cursor, text.length));
    const beforeCursor = text.slice(0, safeCursor);
    const atIndex = beforeCursor.lastIndexOf('@');

    if (atIndex < 0) {
        return null;
    }

    const previousChar = atIndex === 0 ? '' : beforeCursor[atIndex - 1];
    if (previousChar && /[A-Za-z0-9._%+-]/.test(previousChar)) {
        return null;
    }

    const query = beforeCursor.slice(atIndex + 1);
    if (/\s/.test(query) || !/^[A-Za-z0-9._%+-]*$/.test(query)) {
        return null;
    }

    return {
        start: atIndex,
        end: safeCursor,
        query,
    };
}

function toMentionToken(option: AssigneeOption): string {
    const localPart = option.email.split('@')[0]?.trim();
    if (localPart) {
        return localPart;
    }

    return option.name.replace(/\s+/g, '');
}

function renderMessageTextWithMentions(text: string): React.ReactNode {
    const parts = text.split(/(@[A-Za-z0-9._%+-]+)/g);

    return parts.map((part, index) => {
        if (/^@[A-Za-z0-9._%+-]+$/.test(part)) {
            return <span key={`mention-${index}`} className="ticket-message-mention">{part}</span>;
        }

        return <React.Fragment key={`text-${index}`}>{part}</React.Fragment>;
    });
}

const TicketMessageImage: React.FC<{ ticketId: string; message: TicketMessage }> = ({ ticketId, message }) => {
    const [imageUrl, setImageUrl] = useState<string | null>(null);

    useEffect(() => {
        let disposed = false;
        let objectUrl: string | null = null;

        void fetchTicketMessageImage(ticketId, message.id)
            .then((url) => {
                objectUrl = url;
                if (disposed) {
                    URL.revokeObjectURL(url);
                    return;
                }
                setImageUrl(url);
            })
            .catch(() => undefined);

        return () => {
            disposed = true;
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [ticketId, message.id]);

    if (!imageUrl || !message.image) {
        return null;
    }

    return (
        <a href={imageUrl} target="_blank" rel="noreferrer">
            <img className="ticket-message-image" src={imageUrl} alt={message.image.fileName} />
        </a>
    );
};

const Tickets: React.FC = () => {
    const { accounts } = useMsal();
    const location = useLocation();
    const navigate = useNavigate();
    const { googleUserName } = useGoogleAuth();
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const [lookupAttempted, setLookupAttempted] = useState(false);
    const [registrationCustomerName, setRegistrationCustomerName] = useState<string | null>(null);
    const [messageDrafts, setMessageDrafts] = useState<Record<string, MessageDraftState>>({});
    const [messageImages, setMessageImages] = useState<Record<string, AddTicketMessageRequest['image'] | undefined>>({});
    const [form, setForm] = useState<CreateTicketRequest>({
        subject: '',
        serialNumber: '',
        assignedTo: '',
        notes: '',
    });
    const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortKey, setSortKey] = useState<SortKey>('updatedAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [activeQuickFilter, setActiveQuickFilter] = useState<QuickFilterKey>('open');
    const [adminUsers, setAdminUsers] = useState<AssigneeOption[]>([]);
    const [registrationSuggestions, setRegistrationSuggestions] = useState<{ serialNumber: string; customer: string }[]>([]);
    const [serialSuggestionsVisible, setSerialSuggestionsVisible] = useState(false);
    const [seenMessageAtByTicket, setSeenMessageAtByTicket] = useState<Record<string, string>>({});
    const [activeMentionContext, setActiveMentionContext] = useState<MentionContext | null>(null);
    const [mentionMenuOpen, setMentionMenuOpen] = useState(false);
    const [activeMentionIndex, setActiveMentionIndex] = useState(0);
    const [isMessagesScrolled, setIsMessagesScrolled] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const hasInitializedSeenRef = useRef(false);
    const messageComposerRef = useRef<HTMLTextAreaElement | null>(null);
    const mentionCloseTimerRef = useRef<number | null>(null);
    const copyLinkTimerRef = useRef<number | null>(null);
    const hasAppliedDeepLinkRef = useRef(false);

    const currentUserName =
        googleUserName
        ?? (accounts[0]?.idTokenClaims as Record<string, unknown> | undefined)?.['preferred_username'] as string | undefined
        ?? accounts[0]?.username
        ?? '';

    const seenStorageKey = useMemo(
        () => `${TICKET_SEEN_STORAGE_PREFIX}:${normalizeIdentity(currentUserName) || 'anonymous'}`,
        [currentUserName]
    );

    const initializeSeenEntries = useCallback((ticketData: Ticket[]) => {
        if (hasInitializedSeenRef.current) {
            return;
        }

        setSeenMessageAtByTicket((previous) => {
            const next = { ...previous };
            let changed = false;

            ticketData.forEach((ticket) => {
                if (next[ticket.id]) {
                    return;
                }

                const latestMessage = getLatestMessage(ticket);
                const fallbackTimestamp = ticket.updatedAt;
                const seenAt = latestMessage?.createdAt ?? fallbackTimestamp;

                if (seenAt) {
                    next[ticket.id] = seenAt;
                    changed = true;
                }
            });

            return changed ? next : previous;
        });

        hasInitializedSeenRef.current = true;
    }, []);

    const markTicketAsRead = useCallback((ticket: Ticket) => {
        const latestMessage = getLatestMessage(ticket);
        const seenAt = latestMessage?.createdAt ?? ticket.updatedAt ?? new Date().toISOString();

        setSeenMessageAtByTicket((previous) => {
            if (previous[ticket.id] === seenAt) {
                return previous;
            }

            return {
                ...previous,
                [ticket.id]: seenAt,
            };
        });
    }, []);

    const hasUnreadColleagueUpdate = useCallback((ticket: Ticket): boolean => {
        const latestMessage = getLatestMessage(ticket);
        if (!latestMessage) {
            return false;
        }

        if (latestMessage.direction !== 'agent') {
            return false;
        }

        if (normalizeIdentity(latestMessage.author) === normalizeIdentity(currentUserName)) {
            return false;
        }

        const latestMessageMs = parseTimestampMs(latestMessage.createdAt);
        if (latestMessageMs === 0) {
            return false;
        }

        const seenMessageMs = parseTimestampMs(seenMessageAtByTicket[ticket.id]);
        return seenMessageMs === 0 || latestMessageMs > seenMessageMs;
    }, [currentUserName, seenMessageAtByTicket]);

    const setTicketDeepLinkParams = useCallback((ticket: Ticket) => {
        const params = new URLSearchParams(location.search);
        let changed = false;

        if (ticket.id && params.get('ticketId') !== ticket.id) {
            params.set('ticketId', ticket.id);
            changed = true;
        }

        if (ticket.ticketNumber && ticket.ticketNumber > 0) {
            const ticketNumberValue = ticket.ticketNumber.toString();
            if (params.get('ticket') !== ticketNumberValue) {
                params.set('ticket', ticketNumberValue);
                changed = true;
            }
        }

        if (!changed) {
            return;
        }

        navigate(
            {
                pathname: location.pathname,
                search: `?${params.toString()}`,
            },
            { replace: true }
        );
    }, [location.pathname, location.search, navigate]);

    const openTicket = useCallback((ticket: Ticket) => {
        setSelectedTicketId(ticket.id);
        markTicketAsRead(ticket);
        setTicketDeepLinkParams(ticket);
    }, [markTicketAsRead, setTicketDeepLinkParams]);

    const clearTicketDeepLinkParams = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const hadDeepLink = params.has('ticket') || params.has('ticketId') || params.has('ticketNumber');
        if (!hadDeepLink) {
            return;
        }

        params.delete('ticket');
        params.delete('ticketId');
        params.delete('ticketNumber');

        const nextSearch = params.toString();
        navigate(
            {
                pathname: location.pathname,
                search: nextSearch ? `?${nextSearch}` : '',
            },
            { replace: true }
        );
    }, [location.pathname, location.search, navigate]);

    const closeTicket = useCallback(() => {
        setSelectedTicketId(null);
        clearTicketDeepLinkParams();
    }, [clearTicketDeepLinkParams]);

    const getTicketDeepLink = useCallback((ticket: Ticket): string => {
        const params = new URLSearchParams();
        if (ticket.id) {
            params.set('ticketId', ticket.id);
        }
        if (ticket.ticketNumber && ticket.ticketNumber > 0) {
            params.set('ticket', ticket.ticketNumber.toString());
        }

        const query = params.toString();
        const path = '/tickets';
        const origin = window.location.origin;
        return `${origin}${path}${query ? `?${query}` : ''}`;
    }, []);

    const handleCopyTicketLink = useCallback(async (ticket: Ticket) => {
        const url = getTicketDeepLink(ticket);

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(url);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = url;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }

            setLinkCopied(true);
            if (copyLinkTimerRef.current) {
                window.clearTimeout(copyLinkTimerRef.current);
            }
            copyLinkTimerRef.current = window.setTimeout(() => {
                setLinkCopied(false);
                copyLinkTimerRef.current = null;
            }, 1800);
        } catch {
            setError('Link kopieren mislukt.');
        }
    }, [getTicketDeepLink]);

    const refreshTickets = useCallback(async (showLoading: boolean) => {
        try {
            if (showLoading) {
                setLoading(true);
                setError(null);
            }

            const ticketData = await fetchTickets();
            setTickets(ticketData);
            initializeSeenEntries(ticketData);
        } catch (err) {
            console.error(err);
            if (showLoading) {
                setError('Kon tickets niet laden.');
            }
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [initializeSeenEntries]);

    const loadTickets = useCallback(async () => {
        await refreshTickets(true);
        // Admin users zijn optioneel — fout hier mag tickets niet blokkeren
        const adminUserData = await fetchTicketAdminUsers();
        setAdminUsers(adminUserData);
        // Registraties voor autocomplete — fout hier is niet kritiek
        try {
            const regs = await fetchRegistrations();
            setRegistrationSuggestions(regs.map((r) => ({ serialNumber: r.serialNumber, customer: r.customer })));
        } catch {
            // autocomplete werkt niet, geen blocker
        }
    }, [refreshTickets]);

    useEffect(() => {
        void loadTickets();
    }, [loadTickets]);

    useEffect(() => {
        const timer = window.setInterval(() => {
            void refreshTickets(false);
        }, TICKET_POLL_INTERVAL_MS);

        return () => {
            window.clearInterval(timer);
        };
    }, [refreshTickets]);

    useEffect(() => {
        hasInitializedSeenRef.current = false;
        try {
            const raw = localStorage.getItem(seenStorageKey);
            if (raw) {
                setSeenMessageAtByTicket(JSON.parse(raw) as Record<string, string>);
                return;
            }
        } catch {
            // Ignore invalid storage payloads and continue with empty state.
        }

        setSeenMessageAtByTicket({});
    }, [seenStorageKey]);

    useEffect(() => {
        try {
            localStorage.setItem(seenStorageKey, JSON.stringify(seenMessageAtByTicket));
        } catch {
            // Ignore storage write errors (private mode, quota, etc.).
        }
    }, [seenStorageKey, seenMessageAtByTicket]);

    useEffect(() => {
        const serial = form.serialNumber?.trim() ?? '';
        if (!serial) {
            setLookupLoading(false);
            setLookupAttempted(false);
            setRegistrationCustomerName(null);
            return;
        }

        let cancelled = false;
        setLookupLoading(true);

        const timer = setTimeout(async () => {
            try {
                const registration = await fetchRegistration(serial);
                if (!cancelled) {
                    setRegistrationCustomerName(registration.customer ?? null);
                    setLookupAttempted(true);
                }
            } catch {
                if (!cancelled) {
                    setRegistrationCustomerName(null);
                    setLookupAttempted(true);
                }
            } finally {
                if (!cancelled) {
                    setLookupLoading(false);
                }
            }
        }, 350);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [form.serialNumber]);

    const openTicketCount = useMemo(
        () => tickets.filter((t) => t.status !== 'done').length,
        [tickets]
    );

    const selectedTicket = useMemo(
        () => tickets.find((ticket) => ticket.id === selectedTicketId) ?? null,
        [tickets, selectedTicketId]
    );

    const deepLinkTarget = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const ticketId = params.get('ticketId')?.trim() || null;

        const numberCandidate = params.get('ticket')?.trim() || params.get('ticketNumber')?.trim() || '';
        const parsedNumber = Number.parseInt(numberCandidate, 10);
        const ticketNumber = Number.isFinite(parsedNumber) && parsedNumber > 0 ? parsedNumber : null;

        if (!ticketId && ticketNumber === null) {
            return null;
        }

        return { ticketId, ticketNumber };
    }, [location.search]);

    const assigneeOptions = useMemo(() => {
        const map = new Map<string, string>();
        for (const a of adminUsers) {
            map.set(a.email.toLowerCase(), a.name);
        }
        if (currentUserName.trim() && !map.has(currentUserName.trim().toLowerCase())) {
            map.set(currentUserName.trim().toLowerCase(), currentUserName.trim());
        }
        return Array.from(map.entries())
            .map(([email, name]) => ({ email, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'nl-NL'));
    }, [adminUsers, currentUserName]);

    const assigneeLabel = (email?: string | null): string => {
        if (!email) return '—';
        const found = assigneeOptions.find((a) => a.email.toLowerCase() === email.toLowerCase());
        return found ? found.name : email;
    };

    const mentionCandidates = useMemo(() => {
        if (!activeMentionContext) {
            return [];
        }

        const normalizedQuery = activeMentionContext.query.trim().toLowerCase();
        if (!normalizedQuery) {
            return assigneeOptions.slice(0, 8);
        }

        return assigneeOptions
            .filter((option) => {
                const localPart = option.email.split('@')[0]?.toLowerCase() ?? '';
                return option.name.toLowerCase().includes(normalizedQuery)
                    || option.email.toLowerCase().includes(normalizedQuery)
                    || localPart.includes(normalizedQuery);
            })
            .slice(0, 8);
    }, [activeMentionContext, assigneeOptions]);

    const filterCounts = useMemo(() => {
        const now = Date.now();
        const overdueMs = 48 * 60 * 60 * 1000;
        const myName = currentUserName.trim().toLowerCase();

        const open = tickets.filter((ticket) => ticket.status !== 'done').length;
        const waitingCustomer = tickets.filter((ticket) => ticket.status === 'waiting_customer').length;
        const overdue = tickets.filter((ticket) => {
            if (ticket.status === 'done') {
                return false;
            }
            const updatedAt = new Date(ticket.updatedAt).getTime();
            return Number.isFinite(updatedAt) && now - updatedAt > overdueMs;
        }).length;
        const mine = tickets.filter((ticket) => {
            if (!myName) {
                return false;
            }
            return (ticket.assignedTo ?? '').trim().toLowerCase() === myName;
        }).length;

        return {
            all: tickets.length,
            open,
            waiting_customer: waitingCustomer,
            overdue,
            mine,
        };
    }, [tickets, currentUserName]);

    const filteredAndSortedTickets = useMemo(() => {
        const query = searchTerm.trim().toLowerCase();
        const now = Date.now();
        const overdueMs = 48 * 60 * 60 * 1000;
        const myName = currentUserName.trim().toLowerCase();

        const source = tickets.filter((ticket) => {
            switch (activeQuickFilter) {
                case 'waiting_customer':
                    return ticket.status === 'waiting_customer';
                case 'overdue': {
                    if (ticket.status === 'done') {
                        return false;
                    }
                    const updatedAt = new Date(ticket.updatedAt).getTime();
                    return Number.isFinite(updatedAt) && now - updatedAt > overdueMs;
                }
                case 'mine':
                    return Boolean(myName) && (ticket.assignedTo ?? '').trim().toLowerCase() === myName;
                case 'all':
                    return true;
                case 'open':
                default:
                    return ticket.status !== 'done';
            }
        });

        const filtered = query
            ? source.filter((ticket) => {
                const haystack = [
                    ticket.subject,
                    ticket.customerName,
                    ticket.serialNumber,
                    statusLabelMap[ticket.status],
                    ticket.assignedTo,
                ]
                    .filter(Boolean)
                    .join(' ')
                    .toLowerCase();
                return haystack.includes(query);
            })
            : source;

        const sorted = [...filtered].sort((a, b) => {
            if (sortKey === 'updatedAt') {
                const diff = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
                return sortDirection === 'asc' ? diff : -diff;
            }

            const aValue = (a[sortKey] ?? '').toString().toLowerCase();
            const bValue = (b[sortKey] ?? '').toString().toLowerCase();

            if (aValue < bValue) {
                return sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sorted;
    }, [tickets, searchTerm, sortKey, sortDirection, activeQuickFilter, currentUserName]);

    const toggleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setSortKey(key);
        setSortDirection(key === 'updatedAt' ? 'desc' : 'asc');
    };

    const renderSortLabel = (label: string, key: SortKey) => {
        if (sortKey !== key) {
            return label;
        }

        return `${label} ${sortDirection === 'asc' ? '▲' : '▼'}`;
    };

    useEffect(() => {
        if (!currentUserName.trim()) {
            return;
        }

        setForm((prev) => {
            if (prev.assignedTo?.trim()) {
                return prev;
            }

            return {
                ...prev,
                assignedTo: currentUserName,
            };
        });
    }, [currentUserName]);

    const handleCreateTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        const subject = form.subject?.trim() ?? '';
        if (subject.length > MAX_TICKET_SUBJECT_LENGTH) {
            setError(`Onderwerp mag maximaal ${MAX_TICKET_SUBJECT_LENGTH} tekens bevatten.`);
            return;
        }

        if (!form.serialNumber?.trim() || !subject) {
            setError('Serienummer en onderwerp zijn verplicht.');
            return;
        }

        try {
            setSaving(true);
            setError(null);
            const created = await createTicket({
                ...form,
                subject,
                serialNumber: form.serialNumber.trim(),
                assignedTo: form.assignedTo?.trim() || undefined,
                notes: form.notes?.trim() || undefined,
            });
            setTickets((prev) => [created, ...prev]);
            setForm({
                subject: '',
                serialNumber: '',
                assignedTo: '',
                notes: '',
            });
            setLookupLoading(false);
            setLookupAttempted(false);
            setRegistrationCustomerName(null);
        } catch (err) {
            console.error(err);
            setError('Ticket aanmaken mislukt.');
        } finally {
            setSaving(false);
        }
    };

    const handleQuickUpdate = async (ticket: Ticket, patch: Partial<Ticket>) => {
        try {
            const updated = await updateTicket(ticket.id, {
                status: patch.status as TicketStatus | undefined,
                assignedTo: patch.assignedTo,
                notes: patch.notes,
            });
            setTickets((prev) => prev.map((t) => (t.id === ticket.id ? updated : t)));
        } catch (err) {
            console.error(err);
            setError('Ticket bijwerken mislukt.');
        }
    };

    const setDraftValue = useCallback((ticketId: string, patch: Partial<MessageDraftState>) => {
        setMessageDrafts((prev) => ({
            ...prev,
            [ticketId]: {
                ...createDefaultDraft(),
                author: currentUserName,
                ...prev[ticketId],
                ...patch,
            },
        }));
    }, [currentUserName]);

    const updateMentionContext = useCallback((text: string, cursor: number) => {
        const nextContext = findMentionContext(text, cursor);
        setActiveMentionContext(nextContext);
        setActiveMentionIndex(0);
        setMentionMenuOpen(Boolean(nextContext));
    }, []);

    const applyMentionCandidate = useCallback((ticketId: string, option: AssigneeOption) => {
        if (!activeMentionContext) {
            return;
        }

        const currentText = messageDrafts[ticketId]?.text ?? '';
        const mentionToken = `@${toMentionToken(option)}`;
        const nextText = `${currentText.slice(0, activeMentionContext.start)}${mentionToken} ${currentText.slice(activeMentionContext.end)}`;
        const nextCaret = activeMentionContext.start + mentionToken.length + 1;

        setDraftValue(ticketId, { text: nextText });
        setActiveMentionContext(null);
        setMentionMenuOpen(false);

        window.requestAnimationFrame(() => {
            if (!messageComposerRef.current) {
                return;
            }

            messageComposerRef.current.focus();
            messageComposerRef.current.setSelectionRange(nextCaret, nextCaret);
        });
    }, [activeMentionContext, messageDrafts, setDraftValue]);

    const handleAddMessage = async (ticket: Ticket) => {
        const draft = {
            ...createDefaultDraft(),
            author: currentUserName,
            ...messageDrafts[ticket.id],
        };

        const image = messageImages[ticket.id];
        if (!draft.text?.trim() && !image) {
            setError('Berichttekst of afbeelding is verplicht.');
            return;
        }

        try {
            setError(null);
            setDraftValue(ticket.id, { saving: true });

            const updated = await addTicketMessage(ticket.id, {
                channel: draft.channel,
                direction: draft.direction,
                text: draft.text.trim(),
                author: draft.author?.trim() || undefined,
                image,
            });

            setTickets((prev) => prev.map((t) => (t.id === ticket.id ? updated : t)));
            markTicketAsRead(updated);
            setMessageDrafts((prev) => ({
                ...prev,
                [ticket.id]: {
                    ...createDefaultDraft(),
                    author: currentUserName,
                },
            }));
            setMessageImages((prev) => ({ ...prev, [ticket.id]: undefined }));
        } catch (err) {
            console.error(err);
            setError('Bericht toevoegen mislukt.');
            setDraftValue(ticket.id, { saving: false });
        }
    };

    const handleImageSelected = (ticketId: string, file?: File) => {
        if (!file) {
            return;
        }

        const allowedTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type) || file.size > 8 * 1024 * 1024) {
            setError('Kies een PNG, JPEG, GIF of WebP-afbeelding van maximaal 8 MB.');
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const result = typeof reader.result === 'string' ? reader.result : '';
            const commaIndex = result.indexOf(',');
            setMessageImages((prev) => ({
                ...prev,
                [ticketId]: {
                    fileName: file.name,
                    contentType: file.type,
                    dataBase64: commaIndex >= 0 ? result.slice(commaIndex + 1) : result,
                },
            }));
        };
        reader.readAsDataURL(file);
    };

    const getSortedMessages = (messages?: TicketMessage[]): TicketMessage[] => {
        if (!messages || messages.length === 0) {
            return [];
        }

        return [...messages].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
    };

    const unreadColleagueCount = useMemo(
        () => tickets.filter((ticket) => hasUnreadColleagueUpdate(ticket)).length,
        [tickets, hasUnreadColleagueUpdate]
    );

    useEffect(() => {
        if (!selectedTicket) {
            return;
        }

        markTicketAsRead(selectedTicket);
    }, [selectedTicket, markTicketAsRead]);

    useEffect(() => {
        hasAppliedDeepLinkRef.current = false;
    }, [deepLinkTarget]);

    useEffect(() => {
        if (!deepLinkTarget || hasAppliedDeepLinkRef.current || loading || tickets.length === 0) {
            return;
        }

        const found = tickets.find((ticket) => {
            if (deepLinkTarget.ticketId && ticket.id === deepLinkTarget.ticketId) {
                return true;
            }

            if (deepLinkTarget.ticketNumber !== null && ticket.ticketNumber === deepLinkTarget.ticketNumber) {
                return true;
            }

            return false;
        });

        hasAppliedDeepLinkRef.current = true;

        if (found) {
            openTicket(found);
        }
    }, [deepLinkTarget, loading, openTicket, tickets]);

    useEffect(() => {
        setActiveMentionContext(null);
        setMentionMenuOpen(false);
        setActiveMentionIndex(0);
        setIsMessagesScrolled(false);
        setLinkCopied(false);
    }, [selectedTicketId]);

    useEffect(() => () => {
        if (mentionCloseTimerRef.current) {
            window.clearTimeout(mentionCloseTimerRef.current);
            mentionCloseTimerRef.current = null;
        }

        if (copyLinkTimerRef.current) {
            window.clearTimeout(copyLinkTimerRef.current);
            copyLinkTimerRef.current = null;
        }
    }, []);

    return (
        <div className="tickets-page">
            <div className="tickets-header">
                <h1>Tickets</h1>
                <p>
                    {openTicketCount} open tickets
                    {unreadColleagueCount > 0 ? ` • ${unreadColleagueCount} nieuwe collega-update${unreadColleagueCount > 1 ? 's' : ''}` : ''}
                </p>
            </div>

            {error && <p className="tickets-error">{error}</p>}

            {!selectedTicket && (<>
            <form className="ticket-create-form" onSubmit={handleCreateTicket}>
                <div className="ticket-serial-combobox">
                    <input
                        value={form.serialNumber}
                        onChange={(e) => {
                            setForm((prev) => ({ ...prev, serialNumber: e.target.value }));
                            setSerialSuggestionsVisible(true);
                        }}
                        onFocus={() => setSerialSuggestionsVisible(true)}
                        onBlur={() => setTimeout(() => setSerialSuggestionsVisible(false), 150)}
                        placeholder="Serienummer *"
                        autoComplete="off"
                    />
                    {serialSuggestionsVisible && form.serialNumber.trim().length >= 2 && (() => {
                        const q = form.serialNumber.trim().toLowerCase();
                        const matches = registrationSuggestions.filter(
                            (r) => r.serialNumber.toLowerCase().includes(q) || r.customer.toLowerCase().includes(q)
                        ).slice(0, 8);
                        return matches.length > 0 ? (
                            <ul className="ticket-serial-suggestions">
                                {matches.map((r) => (
                                    <li
                                        key={r.serialNumber}
                                        onMouseDown={() => {
                                            setForm((prev) => ({ ...prev, serialNumber: r.serialNumber }));
                                            setSerialSuggestionsVisible(false);
                                        }}
                                    >
                                        <span className="ticket-serial-sn">{r.serialNumber}</span>
                                        <span className="ticket-serial-customer">{r.customer}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : null;
                    })()}
                </div>
                <input
                    value={form.subject}
                    onChange={(e) => setForm((prev) => ({ ...prev, subject: e.target.value }))}
                    placeholder="Onderwerp *"
                />
                {form.subject.length > MAX_TICKET_SUBJECT_LENGTH && (
                    <p className="ticket-form-warning">
                        Onderwerp is {form.subject.length} tekens lang; maximaal {MAX_TICKET_SUBJECT_LENGTH} toegestaan.
                    </p>
                )}
                <select
                    value={form.assignedTo ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, assignedTo: e.target.value }))}
                >
                    <option value="">Niet toegewezen</option>
                    {assigneeOptions.map((a) => (
                        <option key={a.email} value={a.email}>{a.name}</option>
                    ))}
                </select>
                <input
                    value={form.notes}
                    onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                    placeholder="Content ticket (optioneel)"
                />
                <button type="submit" disabled={saving}>
                    {saving ? 'Opslaan...' : 'Ticket maken'}
                </button>
            </form>

            {lookupLoading && <p className="ticket-lookup-info">Klant wordt opgezocht...</p>}
            {!lookupLoading && lookupAttempted && registrationCustomerName && (
                <p className="ticket-lookup-ok">Klant gevonden: {registrationCustomerName}</p>
            )}
            {!lookupLoading && lookupAttempted && !registrationCustomerName && (
                <p className="ticket-lookup-error">Geen registratie gevonden voor dit serienummer.</p>
            )}

            <div className="ticket-list-toolbar">
                <div className="ticket-quick-filters" role="tablist" aria-label="Snelle ticketfilters">
                    <button
                        type="button"
                        className={`ticket-filter-chip${activeQuickFilter === 'all' ? ' active' : ''}`}
                        onClick={() => setActiveQuickFilter('all')}
                    >
                        Alle tickets <span>{filterCounts.all}</span>
                    </button>
                    <button
                        type="button"
                        className={`ticket-filter-chip${activeQuickFilter === 'open' ? ' active' : ''}`}
                        onClick={() => setActiveQuickFilter('open')}
                    >
                        Open <span>{filterCounts.open}</span>
                    </button>
                    <button
                        type="button"
                        className={`ticket-filter-chip${activeQuickFilter === 'waiting_customer' ? ' active' : ''}`}
                        onClick={() => setActiveQuickFilter('waiting_customer')}
                    >
                        Wachten op klant <span>{filterCounts.waiting_customer}</span>
                    </button>
                    <button
                        type="button"
                        className={`ticket-filter-chip${activeQuickFilter === 'overdue' ? ' active' : ''}`}
                        onClick={() => setActiveQuickFilter('overdue')}
                    >
                        Achterstallig <span>{filterCounts.overdue}</span>
                    </button>
                    <button
                        type="button"
                        className={`ticket-filter-chip${activeQuickFilter === 'mine' ? ' active' : ''}`}
                        onClick={() => setActiveQuickFilter('mine')}
                        disabled={!currentUserName.trim()}
                    >
                        Toegewezen aan mij <span>{filterCounts.mine}</span>
                    </button>
                </div>
                <input
                    className="ticket-list-search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Zoek op onderwerp, klant, serienummer, status of toegewezen"
                />
                <span className="ticket-list-count">{filteredAndSortedTickets.length} resultaten</span>
            </div>

            {loading ? (
                <p className="tickets-loading">Tickets laden...</p>
            ) : (
                <div className="ticket-list-wrap">
                    <table className="ticket-list-table">
                        <thead>
                            <tr>
                                <th style={{ width: '4rem' }}>
                                    <button type="button" className="ticket-sort-button" onClick={() => toggleSort('ticketNumber')}>
                                        {renderSortLabel('#', 'ticketNumber')}
                                    </button>
                                </th>
                                <th>
                                    <button type="button" className="ticket-sort-button" onClick={() => toggleSort('serialNumber')}>
                                        {renderSortLabel('Serienummer', 'serialNumber')}
                                    </button>
                                </th>
                                <th>
                                    <button type="button" className="ticket-sort-button" onClick={() => toggleSort('subject')}>
                                        {renderSortLabel('Onderwerp', 'subject')}
                                    </button>
                                </th>
                                <th>
                                    <button type="button" className="ticket-sort-button" onClick={() => toggleSort('customerName')}>
                                        {renderSortLabel('Klant', 'customerName')}
                                    </button>
                                </th>
                                <th>
                                    <button type="button" className="ticket-sort-button" onClick={() => toggleSort('status')}>
                                        {renderSortLabel('Status', 'status')}
                                    </button>
                                </th>
                                <th>
                                    <button type="button" className="ticket-sort-button" onClick={() => toggleSort('assignedTo')}>
                                        {renderSortLabel('Toegewezen', 'assignedTo')}
                                    </button>
                                </th>
                                <th>
                                    <button type="button" className="ticket-sort-button" onClick={() => toggleSort('updatedAt')}>
                                        {renderSortLabel('Bijgewerkt', 'updatedAt')}
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedTickets.map((ticket) => {
                                const hasUnreadColleagueMessage = hasUnreadColleagueUpdate(ticket);

                                return (
                                <tr
                                    key={ticket.id}
                                    className={`ticket-list-row${selectedTicketId === ticket.id ? ' active' : ''}${hasUnreadColleagueMessage ? ' ticket-list-row-unread' : ''}`}
                                    tabIndex={0}
                                    role="button"
                                    onClick={() => openTicket(ticket)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            openTicket(ticket);
                                        }
                                    }}
                                >
                                    <td>{ticket.ticketNumber ?? '-'}</td>
                                    <td>{ticket.serialNumber ?? '-'}</td>
                                    <td>
                                        <div className="ticket-subject-cell">
                                            <span>{ticket.subject}</span>
                                            {hasUnreadColleagueMessage && <span className="ticket-unread-badge">Nieuw</span>}
                                        </div>
                                    </td>
                                    <td>{ticket.customerName ?? '-'}</td>
                                    <td>
                                        <span className={getStatusClass(ticket.status)}>
                                            {statusLabelMap[ticket.status]}
                                        </span>
                                    </td>
                                    <td>{assigneeLabel(ticket.assignedTo)}</td>
                                    <td>{formatDateTime(ticket.updatedAt)}</td>
                                </tr>
                                );
                            })}
                            {filteredAndSortedTickets.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="ticket-list-empty">Geen tickets gevonden.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
            </>)}

            {selectedTicket && (() => {
                const phone = normalizePhone(selectedTicket.customerPhone);
                const whatsappText = encodeURIComponent(
                    `Hallo ${selectedTicket.customerName ?? 'klant'}, over ticket: ${selectedTicket.subject}`
                );
                const whatsappWebUrl = phone
                    ? `https://web.whatsapp.com/send?phone=${phone}&text=${whatsappText}`
                    : null;
                const draft = {
                    ...createDefaultDraft(),
                    author: currentUserName,
                    ...messageDrafts[selectedTicket.id],
                };
                const sortedMessages = getSortedMessages(selectedTicket.messages);

                return (
                    <div className="ticket-fullpage">
                        <article className="ticket-card ticket-fullpage-card">
                            <div className="ticket-card-top">
                                <button
                                    type="button"
                                    className="ticket-modal-close"
                                    onClick={closeTicket}
                                >
                                    ← Terug
                                </button>
                                <div className="ticket-card-heading">
                                    <h3>#{selectedTicket.ticketNumber ?? '?'} — {selectedTicket.subject}</h3>
                                    <button
                                        type="button"
                                        className={`ticket-copy-link-btn${linkCopied ? ' copied' : ''}`}
                                        onClick={() => void handleCopyTicketLink(selectedTicket)}
                                        title="Kopieer deeplink naar dit ticket"
                                    >
                                        {linkCopied ? 'Gekopieerd' : 'Kopieer link'}
                                    </button>
                                </div>
                            </div>

                            <div className="ticket-detail-body">
                                {/* Linkerkolom: kerninfo + acties */}
                                <aside className="ticket-detail-meta">
                                    <div className="ticket-detail-field">
                                        <span className="ticket-field-label">Klant</span>
                                        <span className="ticket-field-value">{selectedTicket.customerName ?? '—'}</span>
                                    </div>
                                    <div className="ticket-detail-field">
                                        <span className="ticket-field-label">Serienummer</span>
                                        {selectedTicket.serialNumber
                                            ? (
                                                <div className="ticket-serial-actions">
                                                    <span className="ticket-field-value">{selectedTicket.serialNumber}</span>
                                                    <a href={`https://report.gjschipper.nl/report/${encodeURIComponent(selectedTicket.serialNumber)}`} target="_blank" rel="noreferrer" className="ticket-serial-report-link">Laatste rapport</a>
                                                    <button
                                                        type="button"
                                                        className="ticket-serial-device-link"
                                                        onClick={() => navigate(`/devices?serialNumber=${encodeURIComponent(selectedTicket.serialNumber as string)}`)}
                                                    >
                                                        Apparaatgegevens →
                                                    </button>
                                                </div>
                                            )
                                            : <span className="ticket-field-value">—</span>}
                                    </div>
                                    <div className="ticket-detail-field">
                                        <span className="ticket-field-label">Telefoon / WhatsApp</span>
                                        <div className="ticket-phone-row">
                                            <span className="ticket-field-value">{selectedTicket.customerPhone || '—'}</span>
                                            {whatsappWebUrl && (
                                                <a
                                                    href={whatsappWebUrl}
                                                    className="ticket-whatsapp"
                                                    title="Open WhatsApp Web"
                                                    aria-label="Open WhatsApp Web"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    ↗
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                    <hr className="ticket-meta-divider" />
                                    <div className="ticket-detail-field">
                                        <span className="ticket-field-label">Status</span>
                                        <select
                                            className={`ticket-inline-input ticket-status-select ticket-status-select--${selectedTicket.status}`}
                                            value={selectedTicket.status}
                                            onChange={(e) =>
                                                void handleQuickUpdate(selectedTicket, {
                                                    status: e.target.value as TicketStatus,
                                                })
                                            }
                                        >
                                            {statusOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="ticket-detail-field">
                                        <span className="ticket-field-label">Toegewezen aan</span>
                                        <select
                                            className="ticket-inline-input"
                                            value={selectedTicket.assignedTo ?? ''}
                                            onChange={(e) => {
                                                const nextAssignedTo = e.target.value;
                                                setTickets((prev) =>
                                                    prev.map((t) =>
                                                        t.id === selectedTicket.id ? { ...t, assignedTo: nextAssignedTo } : t
                                                    )
                                                );
                                                void handleQuickUpdate(selectedTicket, { assignedTo: nextAssignedTo });
                                            }}
                                        >
                                            <option value="">Niet toegewezen</option>
                                            {assigneeOptions.map((a) => (
                                                <option key={a.email} value={a.email}>{a.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="ticket-detail-field">
                                        <span className="ticket-field-label">Content Ticket</span>
                                        <textarea
                                            className="ticket-inline-textarea"
                                            value={selectedTicket.notes ?? ''}
                                            onChange={(e) => {
                                                const nextNotes = e.target.value;
                                                setTickets((prev) =>
                                                    prev.map((t) =>
                                                        t.id === selectedTicket.id ? { ...t, notes: nextNotes } : t
                                                    )
                                                );
                                            }}
                                            onBlur={(e) => {
                                                void handleQuickUpdate(selectedTicket, { notes: e.target.value });
                                            }}
                                            placeholder="Interne ticketinhoud..."
                                        />
                                    </div>
                                </aside>

                                {/* Rechterkolom: tijdlijn */}
                                <section className="ticket-detail-timeline">
                                    <div className="ticket-timeline-header">
                                        <span className="ticket-timeline-side-label">Klant</span>
                                        <span className="ticket-section-label">Tijdlijn</span>
                                        <span className="ticket-timeline-side-label">Support</span>
                                    </div>
                                    <div className={`ticket-message-form ticket-message-composer${isMessagesScrolled ? ' scrolled' : ''}`}>
                                        <textarea
                                            ref={messageComposerRef}
                                            className="ticket-inline-textarea"
                                            value={draft.text ?? ''}
                                            onPaste={(e) => {
                                                const imageItem = Array.from(e.clipboardData.items).find((item) =>
                                                    item.kind === 'file' && item.type.startsWith('image/')
                                                );
                                                const imageFile = imageItem?.getAsFile();
                                                if (!imageFile) {
                                                    return;
                                                }

                                                e.preventDefault();
                                                handleImageSelected(
                                                    selectedTicket.id,
                                                    new File([imageFile], imageFile.name || 'clipboard-image.png', {
                                                        type: imageFile.type,
                                                    })
                                                );
                                            }}
                                            onChange={(e) => {
                                                const text = e.target.value;
                                                const cursor = e.target.selectionStart ?? text.length;
                                                setDraftValue(selectedTicket.id, { text });
                                                updateMentionContext(text, cursor);
                                            }}
                                            onClick={(e) => {
                                                const target = e.currentTarget;
                                                updateMentionContext(target.value, target.selectionStart ?? target.value.length);
                                            }}
                                            onKeyUp={(e) => {
                                                const target = e.currentTarget;
                                                updateMentionContext(target.value, target.selectionStart ?? target.value.length);
                                            }}
                                            onFocus={(e) => {
                                                if (mentionCloseTimerRef.current) {
                                                    window.clearTimeout(mentionCloseTimerRef.current);
                                                    mentionCloseTimerRef.current = null;
                                                }
                                                updateMentionContext(e.currentTarget.value, e.currentTarget.selectionStart ?? e.currentTarget.value.length);
                                            }}
                                            onBlur={() => {
                                                mentionCloseTimerRef.current = window.setTimeout(() => {
                                                    setMentionMenuOpen(false);
                                                }, 120);
                                            }}
                                            onKeyDown={(e) => {
                                                if (!mentionMenuOpen || mentionCandidates.length === 0) {
                                                    return;
                                                }

                                                if (e.key === 'ArrowDown') {
                                                    e.preventDefault();
                                                    setActiveMentionIndex((prev) => (prev + 1) % mentionCandidates.length);
                                                    return;
                                                }

                                                if (e.key === 'ArrowUp') {
                                                    e.preventDefault();
                                                    setActiveMentionIndex((prev) => (prev - 1 + mentionCandidates.length) % mentionCandidates.length);
                                                    return;
                                                }

                                                if (e.key === 'Enter' || e.key === 'Tab') {
                                                    e.preventDefault();
                                                    const selected = mentionCandidates[activeMentionIndex] ?? mentionCandidates[0];
                                                    if (selected) {
                                                        applyMentionCandidate(selectedTicket.id, selected);
                                                    }
                                                    return;
                                                }

                                                if (e.key === 'Escape') {
                                                    e.preventDefault();
                                                    setMentionMenuOpen(false);
                                                }
                                            }}
                                            placeholder="Typ een bericht... (gebruik @naam of @email om iemand te mentionen)"
                                        />
                                        {messageImages[selectedTicket.id] && (
                                            <div className="ticket-image-preview">
                                                <img
                                                    src={`data:${messageImages[selectedTicket.id]?.contentType};base64,${messageImages[selectedTicket.id]?.dataBase64}`}
                                                    alt={messageImages[selectedTicket.id]?.fileName}
                                                />
                                                <button
                                                    type="button"
                                                    className="ticket-image-remove"
                                                    onClick={() => setMessageImages((prev) => ({ ...prev, [selectedTicket.id]: undefined }))}
                                                >
                                                    Verwijderen
                                                </button>
                                            </div>
                                        )}
                                        {mentionMenuOpen && mentionCandidates.length > 0 && (
                                            <ul className="ticket-mention-suggestions" role="listbox" aria-label="Mention suggesties">
                                                {mentionCandidates.map((option, index) => (
                                                    <li
                                                        key={option.email}
                                                        className={`ticket-mention-suggestion${index === activeMentionIndex ? ' active' : ''}`}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            applyMentionCandidate(selectedTicket.id, option);
                                                        }}
                                                        role="option"
                                                        aria-selected={index === activeMentionIndex}
                                                    >
                                                        <span className="ticket-mention-name">{option.name}</span>
                                                        <span className="ticket-mention-email">@{toMentionToken(option)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                        <div className="ticket-message-form-footer">
                                            <div className="ticket-direction-toggle">
                                                {messageDirectionOptions.map((option) => (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        className={`ticket-direction-btn${draft.direction === option.value ? ' active' : ''}`}
                                                        onClick={() => setDraftValue(selectedTicket.id, { direction: option.value as TicketMessageDirection })}
                                                    >
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                            <span className="ticket-message-author">Als: {assigneeLabel(draft.author)}</span>
                                            <label className="ticket-image-upload-button">
                                                Afbeelding
                                                <input
                                                    type="file"
                                                    accept="image/png,image/jpeg,image/gif,image/webp"
                                                    onChange={(e) => {
                                                        handleImageSelected(selectedTicket.id, e.target.files?.[0]);
                                                        e.currentTarget.value = '';
                                                    }}
                                                />
                                            </label>
                                            <button
                                                type="button"
                                                className="ticket-message-button"
                                                disabled={Boolean(draft.saving) || (!draft.text?.trim() && !messageImages[selectedTicket.id])}
                                                onClick={() => void handleAddMessage(selectedTicket)}
                                            >
                                                {draft.saving ? 'Bezig...' : 'Versturen'}
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        className="ticket-messages-scroll"
                                        onScroll={(e) => {
                                            setIsMessagesScrolled(e.currentTarget.scrollTop > 4);
                                        }}
                                    >
                                        {sortedMessages.length === 0 ? (
                                            <p className="ticket-messages-empty">Nog geen berichten in dit ticket.</p>
                                        ) : (
                                            <ul className="ticket-messages-list">
                                                {sortedMessages.map((message) => (
                                                    <li key={message.id} className={`ticket-message-item ticket-message-${message.direction === 'customer' ? 'customer' : 'agent'}`}>
                                                        <div className="ticket-message-meta">
                                                            {message.author && <span>{assigneeLabel(message.author)}</span>}
                                                            <span>{formatDateTime(message.createdAt)}</span>
                                                        </div>
                                                        {message.text && <p className="ticket-message-text">{renderMessageTextWithMentions(message.text)}</p>}
                                                        {message.image && <TicketMessageImage ticketId={selectedTicket.id} message={message} />}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </section>
                            </div>
                        </article>
                    </div>
                );
            })()}
        </div>
    );
};

export default Tickets;
